import React, { useState } from 'react';
import { defineMessages, injectIntl, intlShape } from 'react-intl';
import MarkDown from 'brastrap/editorial/markdown/Markdown';
import ButtonLink from '../../common/button-link/ButtonLink';
import { CONSENT_VALUES } from '../../../../shared/constants/modal';
import ConsentToggle from '../../common/consent-toggle/ConsentToggle';
import ConsentCell from '../../common/consent-cell/ConsentCell';

type Props = {
  collapsedBackgroundColour?: string,
  collapsedTextColour?: string,
  modalId: string,
  onClose: (
    modalId: string,
    redisplayTime: number,
    action: 'granted' | 'denied'
  ) => void,
  redisplayTime: number,
  consentModalText: string,
  modalHeader: string,
  consentModalAcceptButtonText: string,
  consentModalRejectButtonText: string,
  isConsentCookiePresent: Boolean,
  containers: Array[],
};

const messages = defineMessages({
  strictlyNecessaryCookies: {
    id: 'consent-mode.strictlyNecessaryCookies',
    defaultMessage: 'Strictly Necessary Cookies',
  },
  alwaysActive: {
    id: 'consent-mode.alwaysActive',
    defaultMessage: 'Always Active',
  },
  performanceCookies: {
    id: 'consent-mode.performanceCookies',
    defaultMessage: 'Performance Cookies',
  },
  advertisingCookies: {
    id: 'consent-mode.advertisingCookies',
    defaultMessage: 'Advertising Cookies',
  },
});

const STYLE_PREFIX = 'c-consent-modal';

const ConsentModal = (props: Props) => {
  const [consentPreferences, setConstantPreferences] = useState(false);
  const [advertisingToggle, setAdvertisingToggle] = useState(true);
  const [advertisingConsent, setAdvertisingConsent] = useState(
    CONSENT_VALUES.GRANTED
  );
  const [performanceToggle, setPerformanceToggle] = useState(true);
  const [performanceConsent, setPerformanceConsent] = useState(
    CONSENT_VALUES.GRANTED
  );

  const handleConsentToggle = (setToggle, setConsent) => {
    setToggle(prevToggle => {
      setConsent(prevToggle ? CONSENT_VALUES.DENIED : CONSENT_VALUES.GRANTED);
      return !prevToggle;
    });
  };

  const {
    collapsedBackgroundColour,
    collapsedTextColour,
    onClose,
    modalId,
    redisplayTime,
    modalHeader,
    consentModalText,
    consentModalAcceptButtonText,
    consentModalRejectButtonText,
    isConsentCookiePresent,
    containers,
    intl: { formatMessage },
  } = props;

  const { introduction, title } = containers[0];

  if (!isConsentCookiePresent) {
    if (!consentPreferences) {
      return (
        <div
          className={`${STYLE_PREFIX}__container`}
          style={{
            background: collapsedBackgroundColour,
            color: collapsedTextColour,
          }}
        >
          <div className={`${STYLE_PREFIX}__content-container`}>
            <p className={`${STYLE_PREFIX}__text-header`}>
              <b>{modalHeader}</b>
            </p>
            <p className={`${STYLE_PREFIX}__text`}>
              <MarkDown content={consentModalText} />
            </p>
            <div className={`${STYLE_PREFIX}__accept-button-container`}>
              <ButtonLink
                label={consentModalAcceptButtonText}
                modifiers={['major-filled', 'small']}
                onClick={() => {
                  onClose(modalId, redisplayTime, {
                    advertisingConsent: CONSENT_VALUES.GRANTED,
                    performanceConsent: CONSENT_VALUES.GRANTED,
                  });
                }}
              />
            </div>
            <div className={`${STYLE_PREFIX}__reject-link-container`}>
              <ButtonLink
                label={consentModalRejectButtonText}
                modifiers={['underlined', 'minor-hollow', 'xsmall']}
                onClick={() => {
                  setConstantPreferences(true);
                }}
              />
            </div>
          </div>
        </div>
      );
    }
    if (consentPreferences) {
      return (
        <div
          className={`${STYLE_PREFIX}__container`}
          style={{
            background: collapsedBackgroundColour,
            color: collapsedTextColour,
          }}
        >
          <div className={`${STYLE_PREFIX}__consent-preference-container`}>
            <p className={`${STYLE_PREFIX}__text-header-consent-preference`}>
              <b>{title}</b>
            </p>
            <table className={`${STYLE_PREFIX}__consent-table`}>
              <tr>
                <ConsentCell>&#x2022;</ConsentCell>
                <ConsentCell>
                  {formatMessage(messages.strictlyNecessaryCookies)}
                </ConsentCell>
                <ConsentCell>
                  <b>{formatMessage(messages.alwaysActive)}</b>
                </ConsentCell>
              </tr>
              <tr>
                <ConsentCell>&#x2022;</ConsentCell>
                <ConsentCell>
                  {formatMessage(messages.performanceCookies)}
                </ConsentCell>
                <ConsentCell>
                  <ConsentToggle
                    toggleName="performance"
                    checked={performanceToggle}
                    onChange={() =>
                      handleConsentToggle(
                        setPerformanceToggle,
                        setPerformanceConsent
                      )
                    }
                  />
                </ConsentCell>
              </tr>
              <tr>
                <ConsentCell>&#x2022;</ConsentCell>
                <ConsentCell>
                  {formatMessage(messages.advertisingCookies)}
                </ConsentCell>
                <ConsentCell>
                  <ConsentToggle
                    toggleName="marketing"
                    checked={advertisingToggle}
                    onChange={() =>
                      handleConsentToggle(
                        setAdvertisingToggle,
                        setAdvertisingConsent
                      )
                    }
                  />
                </ConsentCell>
              </tr>
            </table>
            <p className={`${STYLE_PREFIX}__consent-cookies-text`}>
              <MarkDown content={introduction} />
            </p>
            <div className={`${STYLE_PREFIX}__accept-button-container`}>
              <ButtonLink
                label="Confirm my Choice"
                modifiers={['major-filled', 'small']}
                onClick={() => {
                  onClose(modalId, redisplayTime, {
                    advertisingConsent,
                    performanceConsent,
                  });
                }}
              />
            </div>
          </div>
        </div>
      );
    }
  }
  return null;
};

ConsentModal.defaultProps = {
  redisplayTime: 5,
  onClose: () => {},
};

ConsentModal.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(ConsentModal);
